/**
 * OS-hands "hands active" handler — the OS_HANDS_ACTIVE ambient signal.
 *
 * The sandbox emits deduped transitions while the OS-hands lane operates the
 * user's real Mac (takeover = the physical cursor/keyboard are held; background
 * = AX/postToPid actuation). We write it into an EPHEMERAL per-task field on the
 * task store (beside sdkStatus); AgentStatusLine reads it and swaps its derived
 * working label for a plain-language "Acting in <App>" / "Operating your Mac"
 * line so a non-technical user knows their machine is being driven.
 *
 * Ephemeral by construction: `handsActive` is absent from persistence.ts's
 * serialized allowlist, so it never survives a reload — a stale "operating your
 * Mac" line after refresh would be a lie. An inactive payload clears the field.
 *
 * Notch: intentionally NOT wired here. The macOS notch presence is a single
 * last-writer-wins slot with only a blind `notch_set_state` setter exposed to
 * the web layer (no generation/label compare, no state stack, no readable
 * current state), so a web-driven invoke would stomp a live voice-converse
 * gesture — worse than no signal. The structured OS_HANDS_ACTIVE payload is
 * documented for a dedicated notch surface in
 * bash-notes/os-hands-notch-contract-2026-07-11.md.
 */
import type { Socket } from "socket.io-client";
import { EVENTS, type OsHandsActivePayload } from "@bash/shared";
import { useTaskStore } from "../taskStore";

export function attachOsHandsHandler(socket: Socket): () => void {
  const onActive = (payload: OsHandsActivePayload) => {
    // Structural validation — the relay is a shared pipe; a malformed frame
    // must never corrupt task state. taskId + active are the only load-bearing
    // fields; ignore anything that doesn't carry them.
    if (
      !payload ||
      typeof payload.taskId !== "string" ||
      typeof payload.active !== "boolean"
    ) {
      return;
    }
    const { taskId, active, app, rung, takeover } = payload;
    useTaskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      task.handsActive = active
        ? {
            active: true,
            app: typeof app === "string" ? app : undefined,
            rung: typeof rung === "number" ? rung : undefined,
            takeover: takeover === true,
          }
        : undefined;
    });
  };

  socket.on(EVENTS.OS_HANDS_ACTIVE, onActive);

  return () => {
    socket.off(EVENTS.OS_HANDS_ACTIVE, onActive);
  };
}
